import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Clock, BrainCircuit, Calendar, Flame, Star, Mic, Printer, Download } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "../lib/api";

function fmtDuration(mins) {
  if (!mins) return "0m";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m}m`;
  if (!m) return `${h}h`;
  return `${h}h ${m}m`;
}

function StatCard({ icon: Icon, label, value, color = "bg-brand-cream", testId }) {
  return (
    <div className={`brutal-card p-4 ${color}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-brand-navy/70 text-xs font-bold uppercase tracking-wide">
        <Icon className="w-4 h-4" strokeWidth={3} />
        {label}
      </div>
      <div className="font-display font-bold text-brand-navy text-3xl mt-1">{value}</div>
    </div>
  );
}

export default function AdminUserReport({ open, userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setData(null);
    api
      .get(`/admin/users/${userId}/report`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load report"))
      .finally(() => setLoading(false));
  }, [open, userId]);

  if (!open) return null;

  const eng = data?.engagement;
  const learn = data?.learning;

  const handlePrint = () => {
    document.body.classList.add("printing-report");
    const cleanup = () => {
      document.body.classList.remove("printing-report");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    // Give the DOM one frame to apply the class before printing
    setTimeout(() => window.print(), 50);
  };

  const handleDownloadCsv = async () => {
    if (!userId) return;
    try {
      const res = await api.get(`/admin/users/${userId}/report.csv`, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const filename = (match && match[1]) || `lingofacil-report-${userId}.csv`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV download failed");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-brand-ink/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 report-overlay"
      onClick={onClose}
    >
      <div
        id="admin-report-print-root"
        className="brutal-card p-6 bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-user-report-modal"
      >
        <div className="flex items-start justify-between mb-4 gap-4">
          <div>
            <div className="brutal-badge bg-brand-yellow text-xs mb-2">User report</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-brand-navy">
              {data?.user?.name || "…"}
            </h3>
            <p className="text-sm text-brand-navy/60">{data?.user?.email}</p>
            {data?.user?.class_year != null && (
              <p className="text-xs text-brand-navy/70 mt-1">
                <span className="brutal-badge bg-brand-blue text-white text-xs">Class {data.user.class_year}</span>
              </p>
            )}
            {data?.user && (
              <p className="text-xs text-brand-navy/50 mt-1 hidden print:block">
                Generated {new Date().toLocaleString()} · LingoFacil admin
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 no-print">
            <button
              onClick={handleDownloadCsv}
              disabled={loading || !data}
              data-testid="admin-report-csv"
              className="brutal-btn-yellow text-xs py-2 disabled:opacity-50"
              title="Download CSV (summary + daily minutes + per-course long-term)"
            >
              <Download className="w-4 h-4" strokeWidth={3} /> CSV
            </button>
            <button
              onClick={handlePrint}
              disabled={loading || !data}
              data-testid="admin-report-print"
              className="brutal-btn-mint text-xs py-2 disabled:opacity-50"
              title="Print or save as PDF"
            >
              <Printer className="w-4 h-4" strokeWidth={3} /> Print
            </button>
            <button
              onClick={onClose}
              data-testid="admin-report-close"
              className="p-2 hover:bg-brand-cream rounded-full"
              title="Close"
            >
              <X className="w-5 h-5" strokeWidth={3} />
            </button>
          </div>
        </div>

        {loading && (
          <div className="py-12 text-center text-brand-navy/60 font-bold">Loading report…</div>
        )}

        {!loading && data && (
          <div className="space-y-6">
            {/* Engagement */}
            <section>
              <h4 className="font-display font-bold text-brand-navy text-lg mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5" strokeWidth={3} /> Time on platform
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Clock} label="Total" value={fmtDuration(eng.total_minutes)} color="bg-brand-mint" testId="report-total-time" />
                <StatCard icon={Calendar} label="Last 7 days" value={fmtDuration(eng.minutes_7d)} color="bg-brand-yellow" testId="report-week-time" />
                <StatCard icon={Calendar} label="Last 30 days" value={fmtDuration(eng.minutes_30d)} color="bg-brand-peach" testId="report-month-time" />
                <StatCard icon={Flame} label="Active days" value={eng.active_days} color="bg-brand-pink text-white" testId="report-active-days" />
              </div>

              <div className="brutal-card bg-white mt-4 p-4">
                <div className="text-xs font-bold text-brand-navy/60 uppercase tracking-wide mb-2">
                  Minutes per day (last 30 days)
                </div>
                <div style={{ width: "100%", height: 200 }} data-testid="report-daily-chart">
                  <ResponsiveContainer>
                    <BarChart data={eng.daily} margin={{ top: 8, right: 8, bottom: 8, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#0b0f1a1a" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d) => d.slice(5)}
                        tick={{ fontSize: 10, fontWeight: 700, fill: "#0b0f1a" }}
                        interval={4}
                      />
                      <YAxis tick={{ fontSize: 10, fontWeight: 700, fill: "#0b0f1a" }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ border: "2px solid #0b0f1a", borderRadius: 8, fontWeight: 700 }}
                        formatter={(v) => [`${v} min`, "Time"]}
                      />
                      <Bar dataKey="minutes" fill="#ff5c8a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {(eng.pages_30d && eng.pages_30d.length > 0) && (
                <div className="brutal-card bg-white mt-4 p-4" data-testid="report-pages-breakdown">
                  <div className="text-xs font-bold text-brand-navy/60 uppercase tracking-wide mb-3">
                    Where time was spent (last 30 days)
                  </div>
                  <div className="space-y-2">
                    {(() => {
                      const total = eng.pages_30d.reduce((a, p) => a + p.minutes, 0) || 1;
                      const label = (k) => ({
                        learn: "Learn", review: "Review", home: "Home",
                        profile: "Profile", create: "Create", courses: "Courses",
                        admin: "Admin", leaderboard: "Ranks", other: "Other",
                      })[k] || k;
                      const swatch = (k) => ({
                        learn: "bg-brand-pink", review: "bg-brand-mint",
                        home: "bg-brand-yellow", profile: "bg-brand-peach",
                        create: "bg-brand-blue", courses: "bg-brand-navy",
                        admin: "bg-brand-ink", leaderboard: "bg-brand-cream",
                        other: "bg-brand-navy/40",
                      })[k] || "bg-brand-navy/40";
                      return eng.pages_30d.map((p) => (
                        <div key={p.page} className="flex items-center gap-3" data-testid={`report-page-${p.page}`}>
                          <div className={`w-3 h-3 rounded-sm border-2 border-brand-ink ${swatch(p.page)}`} />
                          <div className="text-sm font-bold text-brand-navy w-24 shrink-0">{label(p.page)}</div>
                          <div className="flex-1 h-3 bg-brand-cream border-2 border-brand-ink rounded overflow-hidden">
                            <div
                              className={`h-full ${swatch(p.page)}`}
                              style={{ width: `${Math.max(2, (p.minutes / total) * 100)}%` }}
                            />
                          </div>
                          <div className="text-sm font-bold text-brand-navy w-24 text-right">
                            {fmtDuration(p.minutes)} · {Math.round((p.minutes / total) * 100)}%
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </section>

            {/* Learning */}
            <section>
              <h4 className="font-display font-bold text-brand-navy text-lg mb-3 flex items-center gap-2">
                <BrainCircuit className="w-5 h-5" strokeWidth={3} /> Long-term memory
                <span className="text-xs font-normal text-brand-navy/60">
                  (SRS interval ≥ {learn.long_term_threshold_days} days)
                </span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={BrainCircuit} label="Long-term words" value={learn.words_long_term} color="bg-brand-mint" testId="report-long-term" />
                <StatCard icon={Star} label="Learning" value={learn.words_learning} color="bg-brand-yellow" testId="report-learning" />
                <StatCard icon={Calendar} label="Total reviews" value={learn.total_reviews} color="bg-brand-peach" testId="report-total-reviews" />
                <StatCard icon={Mic} label="Speak-back" value={learn.speak_back_attempts} color="bg-brand-blue text-white" testId="report-speakback" />
              </div>

              {learn.long_term_by_course.length > 0 && (
                <div className="brutal-card bg-white mt-4 overflow-hidden">
                  <div className="px-4 py-3 border-b-2 border-brand-ink/10 font-display font-bold text-brand-navy text-sm">
                    Long-term words by course
                  </div>
                  <div className="divide-y-2 divide-brand-ink/10">
                    {learn.long_term_by_course.map((c) => (
                      <div
                        key={c.course_id}
                        className="flex items-center gap-3 px-4 py-3"
                        data-testid={`report-course-${c.course_id}`}
                      >
                        <div className="text-xl">{c.flag || "📚"}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-brand-navy truncate">{c.title}</div>
                          <div className="text-xs text-brand-navy/60">{c.language}</div>
                        </div>
                        <div className="brutal-badge bg-brand-mint text-xs">
                          {c.long_term_words} words
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {learn.long_term_by_course.length === 0 && (
                <div className="text-sm text-brand-navy/60 mt-3">
                  No cards have reached long-term memory yet.
                </div>
              )}
            </section>

            {/* Snapshot */}
            <section className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatCard icon={Star} label="XP" value={data.user.xp} color="bg-brand-cream" testId="report-xp" />
              <StatCard icon={Flame} label="Streak" value={`${data.user.streak}d`} color="bg-brand-cream" testId="report-streak" />
              <StatCard icon={Clock} label="Today" value={fmtDuration(eng.minutes_today)} color="bg-brand-cream" testId="report-today" />
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
